import React, { useState, useEffect } from 'react';
import { BringzzButton, BringzzText } from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
    BringzzCountryDropdown,
    BringzzInput
} from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { useAuth } from 'context/AuthContext';
import { useLocation } from 'react-router-dom';

const ConsumerLocationPage = () => {
    const { backPress } = useNavigation();
    const { navigate } = useNavigation();
    const { user, updateUserAddresses } = useAuth();
    const { sendRequest } = useRequest();
    const location = useLocation();
    const returnTo = location.state?.returnTo || '/register/notification';
    const editAddress = location.state?.editAddress;
    const isEditing = !!editAddress;

    const [formValues, setFormValues] = useState({
        streetName: '',
        houseNumber: '',
        entrance: '',
        floor: '',
        door: '',
        postalCode: '',
        cityName: '',
        note: '',
    });
    const [country, setCountry] = useState({
        name: 'Austria',
        code: 'AT',
    });

    const [selectedLabel, setSelectedLabel] = useState(null);
    const [isActiveAddress, setIsActiveAddress] = useState(true);
    const [isLocating, setIsLocating] = useState(false);
    const [locationError, setLocationError] = useState(null);

    useEffect(() => {
        const initMapKit = async () => {
            if (!window.mapkit) {
                const script = document.createElement('script');
                script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
                document.head.appendChild(script);
                await new Promise((resolve) => { script.onload = resolve; });
            }
            if (!window.mapkit.isInitialized) {
                window.mapkit.init({
                    authorizationCallback: (done) => {
                        done(process.env.REACT_APP_APPLE_MAP_KEY);
                    },
                });
            }
        };
        initMapKit();
    }, []);

    const labelOptions = [
        { id: 'home', icon: 'IconHome', name: 'Home' },
        { id: 'work', icon: 'IconBriefcase', name: 'Work' },
        { id: 'partner', icon: 'IconHeart', name: 'Partner' },
        { id: 'other', icon: 'IconMapMarker', name: 'Other' },
      ];

    // Load address data if editing
    useEffect(() => {
        if (editAddress) {
            setFormValues({
                streetName: editAddress.streetName || '',
                houseNumber: editAddress.houseNumber || '',
                entrance: editAddress.entrance || '',
                floor: editAddress.floor || '',
                door: editAddress.door || '',
                postalCode: editAddress.postalCode || '',
                cityName: editAddress.cityName || '',
                note: editAddress.note || '',
            });
            setCountry({
                name: editAddress.countryName || 'Austria',
                code: editAddress.countryCode || 'AT',
            });
            setSelectedLabel(editAddress.addressLable || null);
        }
    }, [editAddress]);

    const handleUseCurrentLocation = async () => {
        setLocationError(null);
        
        if (!navigator.geolocation) {
            setLocationError("Geolocation is not supported by your browser");
            return;
        }

        setIsLocating(true);
        
        try {
            const position = await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    reject(new Error('Location request timed out. Please try again.'));
                }, 15000);
                
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        clearTimeout(timeoutId);
                        resolve(pos);
                    },
                    (err) => {
                        clearTimeout(timeoutId);
                        reject(err);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 60000
                    }
                );
            });

            const geocoder = new window.mapkit.Geocoder();
            const coordinate = new window.mapkit.Coordinate(position.coords.latitude, position.coords.longitude);
            
            const geocodeResult = await new Promise((resolve, reject) => {
                geocoder.reverseLookup(coordinate, (error, data) => {
                    if (error) {
                        console.error('MapKit geocoding error:', error);
                        reject(error);
                    } else {
                        resolve(data.results[0]);
                    }
                });
            });

            let postalCode = geocodeResult.postalCode || '';
            let streetName = geocodeResult.thoroughfare || '';
            let houseNumber = geocodeResult.subThoroughfare || '';
            let cityName = geocodeResult.locality || geocodeResult.administrativeArea || '';
            let countryName = geocodeResult.country || '';

            if (!postalCode) {
                try {
                    const nominatimResponse = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}&addressdetails=1`
                    );
                    const nominatimData = await nominatimResponse.json();
                    
                    if (nominatimData.address) {
                        postalCode = nominatimData.address.postcode || '';
                        if (!streetName) streetName = nominatimData.address.road || '';
                    }
                } catch (nominatimError) {
                    console.warn('Nominatim geocoding failed:', nominatimError);
                }
            }

            const response = await sendRequest({
                method: "POST",
                url: "/module/address/setLocationAddress",
                data: {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    streetName,
                    houseNumber,
                    cityName,
                    postalCode,
                    countryName,
                }
            });

            if (response.status === "success") {
                if (updateUserAddresses) {
                    await updateUserAddresses();
                }
                navigate(returnTo);
            }
        } catch (error) {
            console.error("Location error:", error);
            let errorMessage = "Could not detect your location";
            
            if (error.code === 1) {
                errorMessage = "Location access denied. Please enable location permissions.";
            } else if (error.code === 2) {
                errorMessage = "Location unavailable. Please try again.";
            } else if (error.code === 3 || error.message?.includes('timeout')) {
                errorMessage = "Location request timed out. Please try again.";
            }
            
            setLocationError(errorMessage);
        } finally {
            setIsLocating(false);
        }
    };

    const saveAddress = async () => {
        try {
            let geoLatitude = editAddress?.geoLatitude || null;
            let geoLongitude = editAddress?.geoLongitude || null;
            
            // Geocode for manual address entry
            try {
                const geocoder = new window.mapkit.Geocoder();
                const addressString = `${formValues.streetName} ${formValues.houseNumber}, ${formValues.cityName}, ${formValues.postalCode}, ${country.name}`;
                
                const geocodeResult = await new Promise((resolve, reject) => {
                    geocoder.lookup(addressString, (error, data) => {
                        if (error || !data.results || data.results.length === 0) {
                            reject(error || new Error('No results found'));
                        } else {
                            resolve(data.results[0]);
                        }
                    });
                });
                
                geoLatitude = geocodeResult.coordinate.latitude;
                geoLongitude = geocodeResult.coordinate.longitude;
            } catch (geocodeError) {
                console.error('Geocoding failed:', geocodeError);
            }
            
            const addressData = {
                belongsTo: user?.integrationId || null,
                streetName: formValues.streetName || null,
                houseNumber: formValues.houseNumber || null,
                cityName: formValues.cityName || null,
                postalCode: formValues.postalCode || null,
                region: formValues.region || null,
                countryName: formValues.countryName || country.name || null,
                entrance: formValues.entrance || null,
                floor: formValues.floor || null,
                door: formValues.door || null,
                note: formValues.note || null,
                addressLable: selectedLabel || null,
                addressIcon: labelOptions.find(label => label.id === selectedLabel)?.icon || null,
                geoLatitude: geoLatitude || null,
                geoLongitude: geoLongitude || null,
            };

            let response;
            
            if (isEditing) {
                // Update existing address
                response = await sendRequest({
                    method: 'PUT',
                    url: '/module/address/updateAddress',
                    data: {
                        integrationId: editAddress.integrationId,
                        addressData
                    },
                });
            } else {
                // Create new address
                response = await sendRequest({
                    method: 'POST',
                    url: '/module/address/setAddress',
                    data: { addressData },
                });
                
                // Set as active address if it's a new address
                if (response.status === 'success') {
                    await sendRequest({
                        method: 'POST',
                        url: '/module/address/setActiveAddress',
                        data: { integrationId: response.data.integrationId }
                    });
                }
            }

            if (response.status === 'success') {
                if (updateUserAddresses) {
                    await updateUserAddresses();
                }
                navigate(returnTo);
            }
        } catch (error) {
            console.error(`Failed to ${isEditing ? 'update' : 'add'} address`, error);
        }
    };

    const handleSave = () => {
        saveAddress();
    };

    return (
        <BringzzPageContainer
            className="bg-white"
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconClose'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: "Location"
            }}
            footer={
                <div className='pb-8 px-6 space-y-3'>
                    <BringzzButton
                        size='lg'
                        className='bg-blue-500 w-full flex justify-center text-white'
                        onClick={handleUseCurrentLocation}
                        disabled={isLocating}
                    >
                        {isLocating ? 'Detecting Location...' : 'Use Current Location'}
                    </BringzzButton>
                    <BringzzButton
                        size='lg'
                        className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
                        onClick={handleSave}
                    >
                        {isEditing ? 'Update Address' : 'Save Manual Address'}
                    </BringzzButton>
                </div>
            }
            authenticated={false}
        >
            <div className='relative flex justify-center bg-magic-lilac/20'>
                <div className='relative bg-white w-full bg-cover bg-center flex flex-col'>
                    <div className='flex-grow flex flex-col py-8 px-4'>
                        <div className='z-10 flex flex-col '>
                            <div className='pl-2 flex flex-col space-y-6 md:max-w-lg lg:max-w-xl xl:max-w-2xl'>
                                <BringzzText tag='h1' className='pl-2 text-midnight-blue font-bold'>
                                    {isEditing ? 'Edit Address' : 'Add Your Address'}
                                </BringzzText>
                                {locationError && (
                                    <BringzzText tag='p' className='text-red-500 pl-2'>
                                        {locationError}
                                    </BringzzText>
                                )}
                            </div>
                            <div className='mt-6'>
                                <div className='space-y-6 mt-4'>
                                    <BringzzText tag="h4" className="font-bold mb-2">
                                        Address label (optional)
                                    </BringzzText>
                                    <div className="flex justify-around">
                                        {labelOptions.map((label) => (
                                            <div
                                                key={label.id}
                                                className="flex flex-col cursor-pointer"
                                                onClick={() => setSelectedLabel(label.id)}
                                            >
                                                <div
                                                    className={`w-10 h-10 flex items-center justify-center rounded-full border ${
                                                        selectedLabel === label.id
                                                            ? 'bg-blue-500 border-blue-500'
                                                            : 'bg-gray-200 border-gray-200'
                                                    }`}
                                                >
                                                    <BringzzIcon
                                                        size="24"
                                                        icon={label.icon}
                                                        folder="LineIcons"
                                                        className={selectedLabel === label.id ? 'text-white' : 'text-black'}
                                                    />
                                                </div>
                                                <BringzzText tag="span" className="mt-1 text-sm">
                                                    {label.name}
                                                </BringzzText>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex gap-3">
                                        <BringzzInput
                                            id="streetName"
                                            placeholder="Musterstarsse"
                                            label="Street Name"
                                            value={formValues.streetName}
                                            onChange={(e) => setFormValues({ ...formValues, streetName: e.target.value })}
                                            containerClassname="w-full"
                                        />
                                        <BringzzInput
                                            id="houseNumber"
                                            placeholder="12"
                                            label="House Number"
                                            value={formValues.houseNumber}
                                            onChange={(e) => setFormValues({ ...formValues, houseNumber: e.target.value })}
                                            containerClassname="w-1/2"
                                        />
                                    </div>
                                    
                                    <div className="flex gap-3">
                                        <BringzzInput
                                            id="entrance"
                                            placeholder="entrance"
                                            label="Entrance (opt.)"
                                            value={formValues.entrance}
                                            onChange={(e) => setFormValues({ ...formValues, entrance: e.target.value })}
                                            containerClassname="w-full"
                                        />
                                        <BringzzInput
                                            id="floor"
                                            placeholder="floor"
                                            label="Floor (opt.)"
                                            value={formValues.floor}
                                            onChange={(e) => setFormValues({ ...formValues, floor: e.target.value })}
                                            containerClassname="w-full"
                                        />
                                        <BringzzInput
                                            id="door"
                                            label="Door (opt.)"
                                            placeholder="door"
                                            value={formValues.door}
                                            onChange={(e) => setFormValues({ ...formValues, door: e.target.value })}
                                            containerClassname="w-full"
                                        />
                                    </div>
                                    
                                    <div className="flex gap-3">
                                        <BringzzInput
                                            id="postalCode"
                                            placeholder="1040"
                                            label="Postal Code"
                                            value={formValues.postalCode}
                                            onChange={(e) => setFormValues({ ...formValues, postalCode: e.target.value })}
                                            containerClassname="w-1/3"
                                        />
                                        <BringzzInput
                                            id="cityName"
                                            label="City"
                                            placeholder="Wien"
                                            value={formValues.cityName}
                                            onChange={(e) => setFormValues({ ...formValues, cityName: e.target.value })}
                                            containerClassname="w-full"
                                        />
                                    </div>
                                    
                                    <div>
                                        <BringzzText tag="h4" className="font-bold mb-2">
                                            Country
                                        </BringzzText>
                                        <BringzzCountryDropdown
                                            className="w-full bg-[#F4F4F4] border border-gray-300 rounded-lg p-3 text-sm"
                                            mode="flag-name"
                                            value={country}
                                            onCountryChange={(selectedCountry) => {
                                                setCountry(selectedCountry);
                                                setFormValues({ ...formValues, countryName: selectedCountry.name });
                                            }}
                                        />
                                    </div>
                                    
                                    <BringzzInput
                                        id="note"
                                        label="Note to rider"
                                        placeholder="e.g. entrance description, doorbell, etc."
                                        value={formValues.note}
                                        onChange={(e) => setFormValues({ ...formValues, note: e.target.value })}
                                        containerClassname="w-full"
                                        inputClassname="resize-none h-24"
                                        as="textarea"
                                    />
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default ConsumerLocationPage;
